/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import imgixLib from 'js/lib/imgix';

import { Grid, Link, Typography2, breakpoints } from '@coursera/cds-core';
import { CardV2 } from '@coursera/coursera-ui';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { numberOfRelatedCoursesForDomains } from 'bundles/browse/components/Domain/constants/domainData';
import { buildNewWindowOnClickViaUrl } from 'bundles/common/utils/newWindowUtils';
import { getLowQualityImageSrc } from 'bundles/page/components/shared/utils/lazyImgixUtils';

import _t from 'i18n!nls/front-page';

const EVENTING_DATA = {
  DOMAIN_PILL_BUTTON: 'lohp_browse_domain',
  DOMAIN_PAGE_SECTION: 'browse_domains',
} as const;

const THUMBNAIL_COMPRESSION_PARAMS = { fit: 'crop', auto: 'format,compress', height: '100' };

const styles = {
  container: () => css`
    display: block;
    flex-basis: 25%;
    padding-right: 20px;
    margin-bottom: 20px;
    text-decoration: none;

    &:hover {
      text-decoration: none;
    }
    ${breakpoints.down('md')} {
      flex-basis: 33.3%;
    }

    ${breakpoints.down('sm')} {
      flex-basis: 50%;
    }

    ${breakpoints.down('xs')} {
      flex-basis: 100%;
      padding-right: 0;
    }

    .domain-card {
      background-color: white;
      height: 100%;
    }
  `,
  cardContent: css`
    align-items: center;
  `,
  thumbnail: css`
    width: 80px;
    min-width: 80px;
    height: 80px;
    margin-right: 10px;
    background-repeat: no-repeat;
    background-size: cover;
  `,
  title: css`
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    color: #1f1f1f;
  `,
  courseCount: css`
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    color: #636363;
  `,
};

type Props = {
  id: string;
  slug: string;
  name: string;
  imageUrl: string;
  useLazyImage?: boolean;
};

const DomainPill = ({ id, name, slug, imageUrl, useLazyImage }: Props) => {
  const href = `/browse/${id}`;
  const backgroundImage = useLazyImage
    ? getLowQualityImageSrc({ src: imageUrl, maxWidth: 80, ...THUMBNAIL_COMPRESSION_PARAMS, maxHeight: 80 })
    : imgixLib.processImage(imageUrl, THUMBNAIL_COMPRESSION_PARAMS);
  const track = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLAnchorElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTING_DATA.DOMAIN_PILL_BUTTON,
      linkType: 'internal',
      linkName: slug,
      linkURL: href,
    },
    pageSection: {
      sectionName: EVENTING_DATA.DOMAIN_PAGE_SECTION,
    },
  });

  const handleClick = () => {
    track('click_button', {
      button: {
        name: EVENTING_DATA.DOMAIN_PILL_BUTTON,
        linkType: 'internal',
        linkName: slug,
        linkURL: href,
      },
      pageSection: {
        sectionName: EVENTING_DATA.DOMAIN_PAGE_SECTION,
      },
    });
  };

  const numberOfCourses = numberOfRelatedCoursesForDomains[slug || id];

  return (
    <Link
      component="a"
      css={styles.container}
      href={href}
      onClick={buildNewWindowOnClickViaUrl({ href, onClick: handleClick })}
      ref={visibilityTrackingRef}
    >
      <CardV2 rootClassName="domain-card" isInteractive>
        <Grid container alignItems="center">
          <div css={styles.thumbnail} style={{ backgroundImage: `url(${backgroundImage})` }} />
          <Grid item xs={8} data-e2e="domain-pill-label-box" direction="column">
            <Typography2 component="div" variant="bodySecondary" css={styles.title}>
              {name}
            </Typography2>
            {numberOfCourses && (
              <Typography2 component="span" color="supportText" variant="bodySecondary" css={styles.courseCount}>
                {_t('#{numberOfCourses} courses', { numberOfCourses })}
              </Typography2>
            )}
          </Grid>
        </Grid>
      </CardV2>
    </Link>
  );
};

export default DomainPill;
